import { Breadcrumb } from "react-bootstrap";
import "./profileDetails.css";
import FREEPLANIMG from "../Assets/freeplan-img.png";
import PREMIUMPLANIMG from "../Assets/premiumplan-img.png";
import BUSINESSPLANIMG from "../Assets/businessplan-img.png";
import { Link } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import { useEffect, useState } from "react";
import {
  FaQuestionCircle,
  FaKey,
  FaCreditCard,
  FaCheck,
  FaCalendarAlt,
  FaQrcode,
  FaChartLine,
  FaUserEdit,
  FaMedal,
  FaClock,
} from "react-icons/fa";
import { IoIosCreate } from "react-icons/io";

const ProfileDetails = () => {
  const { user, roles } = UserAuth();
  const { premium, business } = roles;
  const [photoError, setPhotoError] = useState(false);
  const [loading, setLoading] = useState(true);

  // State for user stats
  const [userStats, setUserStats] = useState({
    qrCodesCreated: 0,
    scanCount: 0,
    daysActive: 0,
  });

  // State for account information
  const [accountInfo, setAccountInfo] = useState({
    joinDate: "",
    lastLogin: "",
  });

  useEffect(() => {
    // Fetch user stats when component mounts
    if (user) {
      fetchUserStats();
    }
  }, [user]);

  // Function to fetch user stats from API
  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/stats`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Check if response is ok (status in the range 200-299)
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      const { userStats, accountInfo } = data.data;

      // Update state with real data
      setUserStats(userStats);
      setAccountInfo(accountInfo);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching user stats:", error);
      // If there's an error, keep the loading state false but use default values
      setLoading(false);
    }
  };

  // Generate initial avatar if no profile photo
  const getInitials = () => {
    return user?.displayName
      ? user.displayName
          .split(" ")
          .map((name: string) => name[0])
          .join("")
          .toUpperCase()
      : "?";
  };

  return (
    <>
      {user && (
        <div className="profileDetails" role="main">
          <div className="qrCodeList__header">
            <div className="qrCodeList__header-left">
              <h1 className="qrCodeList__title">Dashboard</h1>
              <Breadcrumb className="overview__container__breadcrumbs">
                <Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
                <Breadcrumb.Item active>Dashboard</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
          <div className="profileDetails__container">
            <div
              className="profileDetails__container__details profile-card"
              role="region"
              aria-labelledby="profile-heading"
            >
              <div className="avatar-container">
                {user!.photoURL && !photoError ? (
                  <img
                    src={user!.photoURL}
                    alt={`${user!.displayName}'s profile avatar`}
                    onError={() => setPhotoError(true)}
                    className="profile-avatar"
                  />
                ) : (
                  <div className="initial-avatar">{getInitials()}</div>
                )}
              </div>
              <h2 id="profile-heading">{user!.displayName}</h2>
              <p className="user-email">{user!.email}</p>

              {/* User Stats Section */}
              <div className="user-stats">
                <div className="user-stat-item">
                  <span className="user-stat-value">
                    {loading ? "-" : userStats.qrCodesCreated}
                  </span>
                  <span className="user-stat-label">QR Codes</span>
                </div>
                <div className="user-stat-item">
                  <span className="user-stat-value">
                    {loading ? "-" : userStats.scanCount}
                  </span>
                  <span className="user-stat-label">Total Scans</span>
                </div>
                <div className="user-stat-item">
                  <span className="user-stat-value">
                    {loading ? "-" : userStats.daysActive}
                  </span>
                  <span className="user-stat-label">Days Active</span>
                </div>
              </div>

              {/* Account Info Section */}
              <div className="user-account-info">
                <div className="user-account-info-item">
                  <FaCalendarAlt /> Joined{" "}
                  {loading ? "..." : accountInfo.joinDate}
                </div>
                <div className="user-account-info-item">
                  <FaClock /> Last login:{" "}
                  {loading ? "..." : accountInfo.lastLogin}
                </div>
                <div className="user-account-info-item">
                  <FaMedal />{" "}
                  {premium
                    ? "Premium Member"
                    : business
                    ? "Business Member"
                    : "Free Member"}
                </div>
              </div>

              {/* User Actions */}
              <div className="user-actions">
                <Link to="/dashboard/analytics" className="user-action-btn">
                  <FaChartLine /> Analytics
                </Link>
                <Link to="/dashboard/qrcodes" className="user-action-btn">
                  <FaQrcode /> QR Codes
                </Link>
                <Link
                  to="/dashboard/qrcodes/create"
                  className="user-action-btn"
                >
                  <IoIosCreate /> QR Code Creation
                </Link>
                <button className="user-action-btn">
                  <FaQuestionCircle /> Help
                </button>
                <button className="user-action-btn">
                  <FaKey /> Reset Password
                </button>
                <Link
                  to="https://billing.stripe.com/p/login/test_14k2c83jQ2Tf3UQ000"
                  className="user-action-btn"
                >
                  <FaCreditCard /> Billing
                </Link>
              </div>
            </div>

            {!premium && !business && (
              <div
                className="profileDetails__container__details plan-card free-plan"
                role="region"
                aria-labelledby="plan-heading"
              >
                <img
                  src={FREEPLANIMG}
                  alt="Free plan features illustration"
                  className="plan-img"
                />
                <h2 id="plan-heading">You're on the Free Plan</h2>
                <ul className="plan-features">
                  <li>
                    <FaCheck /> Basic QR code generation
                  </li>
                  <li>
                    <FaCheck /> Limited analytics
                  </li>
                  <li>
                    <FaCheck /> Standard templates
                  </li>
                </ul>
                <Link
                  to="/dashboard/plans"
                  className="plan-cta-button"
                  aria-label="Upgrade to Premium plan"
                >
                  Upgrade to Premium
                </Link>
              </div>
            )}

            {premium && !business && (
              <div
                className="profileDetails__container__details plan-card premium-plan"
                role="region"
                aria-labelledby="premium-heading"
              >
                <img
                  src={PREMIUMPLANIMG}
                  alt="Premium plan features illustration"
                  className="plan-img"
                />
                <h2 id="premium-heading">You're on the Premium Plan</h2>
                <ul className="plan-features">
                  <li>
                    <FaCheck /> Advanced QR customization
                  </li>
                  <li>
                    <FaCheck /> Full analytics dashboard
                  </li>
                  <li>
                    <FaCheck /> Premium templates
                  </li>
                  <li>
                    <FaCheck /> Priority support
                  </li>
                </ul>
                <Link
                  to="/dashboard/plans"
                  className="plan-cta-button"
                  aria-label="View Premium plan benefits"
                >
                  View All Benefits
                </Link>
              </div>
            )}

            {business && (
              <div
                className="profileDetails__container__details plan-card business-plan"
                role="region"
                aria-labelledby="business-heading"
              >
                <img
                  src={BUSINESSPLANIMG}
                  alt="Business plan features illustration"
                  className="plan-img"
                />
                <h2 id="business-heading">You're on the Business Plan</h2>
                <ul className="plan-features">
                  <li>
                    <FaCheck /> Enterprise-level customization
                  </li>
                  <li>
                    <FaCheck /> Advanced analytics & reporting
                  </li>
                  <li>
                    <FaCheck /> Team collaboration features
                  </li>
                  <li>
                    <FaCheck /> API access
                  </li>
                  <li>
                    <FaCheck /> Dedicated support
                  </li>
                </ul>
                <Link
                  to="/dashboard/plans"
                  className="plan-cta-button"
                  aria-label="View Business plan benefits"
                >
                  Manage Your Plan
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileDetails;
